import { useState, useMemo } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts'
import { Segmented } from 'antd'

interface SatisfactionPoint {
  date: string
  satisfiedCount?: number
  neutralCount?: number
  unsatisfiedCount?: number
}

interface Props {
  weekData?: SatisfactionPoint[]
  monthData?: SatisfactionPoint[]
}

export function SatisfactionChart({ weekData = [], monthData = [] }: Props) {
  const [timeRange, setTimeRange] = useState<string>('Tuần')

  const processedChartData = useMemo(() => {
    const rawData = timeRange === 'Tuần' ? weekData : monthData

    return rawData.map((item) => ({
      date: item.date,
      satisfied: item.satisfiedCount ?? 0,
      normal: item.neutralCount ?? 0,
      unsatisfied: item.unsatisfiedCount ?? 0,
    }))
  }, [timeRange, weekData, monthData])

  const colors = {
    satisfied: '#21926B',
    normal: '#F3C409',
    unsatisfied: '#FF383C',
  }

  return (
    <div className="flex h-full w-full flex-col rounded-2xl border border-solid border-gray-300 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between ml-3 mb-4">
        <h3 className="text-[18px] font-bold uppercase text-[#1F2937]">
          Đánh giá theo thời gian
        </h3>
        <Segmented
          options={['Tuần', 'Tháng']}
          value={timeRange}
          onChange={(value) => setTimeRange(value)}
          className="!rounded-full border border-solid border-gray-300 bg-white [&_.ant-segmented-item-selected]:!bg-[#CE7A581A] [&_.ant-segmented-item-selected]:!text-[#CE7A58]"
        />
      </div>

      <div className="min-h-[280px] w-full flex-1">
        <ResponsiveContainer
          width="100%"
          height="100%"
          initialDimension={{ width: 340, height: 280 }}
        >
          <LineChart
            data={processedChartData}
            margin={{ top: 30, right: 10, left: -10, bottom: 10 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#E5E7EB"
            />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12, fill: '#909090', dy: 10 }}
              tickLine={false}
              padding={{ left: 30, right: 30 }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#909090' }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip cursor={false} />

            {/* Đường Hài lòng */}
            <Line
              type="linear"
              dataKey="satisfied"
              name="Lần đánh giá hài lòng"
              stroke={colors.satisfied}
              strokeWidth={1.5}
              dot={{ r: 4, fill: colors.satisfied, strokeWidth: 0 }}
              activeDot={{ r: 6, cursor: 'pointer' }}
            >
              <LabelList
                dataKey="satisfied"
                position="top"
                fill={colors.satisfied}
                fontSize={11}
                fontWeight={600}
                offset={8}
              />
            </Line>

            {/* Đường Bình thường */}
            <Line
              type="linear"
              dataKey="normal"
              name="Lần đánh giá bình thường"
              stroke={colors.normal}
              strokeWidth={1.5}
              dot={{ r: 4, fill: colors.normal, strokeWidth: 0 }}
              activeDot={{ r: 6, cursor: 'pointer' }}
            >
              <LabelList
                dataKey="normal"
                position="top"
                fill={colors.normal}
                fontSize={11}
                fontWeight={600}
                offset={8}
              />
            </Line>

            {/* Đường Không hài lòng */}
            <Line
              type="linear"
              dataKey="unsatisfied"
              name="Lần đánh giá không hài lòng"
              stroke={colors.unsatisfied}
              strokeWidth={1.5}
              dot={{ r: 4, fill: colors.unsatisfied, strokeWidth: 0 }}
              activeDot={{ r: 6, cursor: 'pointer' }}
            >
              <LabelList
                dataKey="unsatisfied"
                position="top"
                fill={colors.unsatisfied}
                fontSize={11}
                fontWeight={600}
                offset={8}
              />
            </Line>
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Legend Chú thích bên dưới */}
      <div className="flex flex-wrap justify-center gap-6 text-[12px] font-medium pt-2">
        <div
          className="flex items-center gap-2"
          style={{ color: colors.satisfied }}
        >
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: colors.satisfied }}
          />
          <span>Lần đánh giá hài lòng</span>
        </div>

        <div
          className="flex items-center gap-2"
          style={{ color: colors.normal }}
        >
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: colors.normal }}
          />
          <span>Lần đánh giá bình thường</span>
        </div>

        <div
          className="flex items-center gap-2"
          style={{ color: colors.unsatisfied }}
        >
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: colors.unsatisfied }}
          />
          <span>Lần đánh giá không hài lòng</span>
        </div>
      </div>
    </div>
  )
}
